import React from 'react';

import { useIntl } from 'react-intl-next';

import Heading from '@atlaskit/heading';
import EmailIcon from '@atlaskit/icon/core/migration/email';
import Lozenge from '@atlaskit/lozenge';
import { Box, Flex, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { ResponderAvatar, useResponderNames } from '@atlassian/eoc-responder-names';
import { Skeleton } from '@atlassian/eoc-skeleton';

import { filterNoOne, getResponder } from '../../../../utils/responder';

import { messages } from './messages';
import { SvgCorrection } from './styled';
import { type ContactProps } from './types';

const nameWrapperStyles = xcss({
	margin: '0',
	width: '192px',
});

const fieldStyles = xcss({
	color: 'color.text.subtlest',
	font: token('font.body'),
});

const Contact = ({ responder, id }: ContactProps) => {
	const intlApi = useIntl();

	const users = useResponderNames(filterNoOne([responder]));
	const user = users[0];

	// TODO: handle error. See: TARDIS-7446
	const { name, email, deleted, type } = getResponder(responder, user, intlApi);

	const isNoOne = type === 'noone';
	const isDeleted = deleted;
	const isDeactivated = type === 'user' && deleted && name;
	const userHasAvatar = user && 'avatarUrl' in user;

	const showEmail = !isDeleted && email;

	const AvatarWrapper = userHasAvatar ? React.Fragment : SvgCorrection;

	return (
		<Stack space={showEmail ? 'space.100' : 'space.150'}>
			<Flex columnGap="space.100" alignItems="center">
				{!isNoOne && (
					<AvatarWrapper>
						<ResponderAvatar
							appearance={responder.type === 'user' ? 'circle' : 'square'}
							size="medium"
							responder={user}
							borderColor="transparent"
						/>
					</AvatarWrapper>
				)}
				{user?.isLoading ? (
					<Skeleton width="100px" height="14px" variant="square" />
				) : (
					<Box xcss={nameWrapperStyles}>
						<Heading id={id} as="h2" size="xsmall">
							{name}
						</Heading>
					</Box>
				)}
			</Flex>

			{showEmail && (
				<Flex alignItems="center" columnGap="space.100">
					<EmailIcon
						color="currentColor"
						label={intlApi.formatMessage(messages.email)}
						LEGACY_size="small"
					/>
					<Box xcss={fieldStyles} as="span">
						{email}
					</Box>
				</Flex>
			)}

			{isDeactivated && (
				<Box>
					<Lozenge appearance="removed">
						{intlApi.formatMessage(messages.accountDeactivated)}
					</Lozenge>
				</Box>
			)}
		</Stack>
	);
};

export { Contact };
